<template>
  <div class="relative w-screen h-20 bg-[--header-bottom-bg-color] shadow-md">

    <!-- FLOATING CONTROLS -->
    <div class="absolute left-0 top-0 h-16 flex items-center px-4 gap-2">
      <LanguageSelector
        v-if="$context.availableLanguages && $context.availableLanguages.length > 1"
      />
      <CurrencySelector
        v-if="$context.availableCurrencies && $context.availableCurrencies.length > 1"
      />
    </div>

      <div  class="w-full px-6 lg:px-16 h-full flex items-center justify-between">
        <!-- LEFT -->
        <div class="flex flex-1 items-center gap-4">
          <a :href="getExternalLink()" target="_blank" class="flex items-center shrink-0" aria-label="Home">
            <img
              src="/static/images/logo.svg"
              alt="AIoD"
              width="105"
              height="60"
              class="w-[105px] h-[60px] object-contain block"
            />
          </a>

          <template v-if="organization">
            <div class="self-stretch border-l border-gray-300"></div>
            <div class="flex flex-col leading-tight text-secondary">
              <span class="text-xs">
                Company
              </span>
              <span class="text-sm font-medium font-bold">
                {{ organization.name }}
              </span>
            </div>
          </template>
        </div>

        <!-- CENTER -->
        <div class="flex-1 flex justify-center">
          <div class="hidden lg:flex items-center gap-4">
            <VcButton
              @click="() => {
                activeHeaderButton = 'marketplace'
                redirect('marketplace')
              }"
              variant="header"
              color="neutral"
              size="sm"
              class="text-sm text-secondary focus:outline-none !focus:rounded uppercase"
              :class="{ 'bg-white': isActive('/catalog') }"
            >
              {{ $t("common.buttons.marketplace") }}
            </VcButton>

            <VcButton
              @click="() => {
                activeHeaderButton = 'business_navigator'
                redirect('business_navigator')
              }"
              target="_blank"
              variant="header"
              color="neutral"
              size="sm"
              class="text-sm text-secondary focus:outline-none rounded uppercase"
            >
              {{ $t("common.buttons.business_navigator") }}
            </VcButton>
          </div>
        </div>

        <!-- RIGHT -->
        <div class="flex items-center gap-4 flex-1 justify-end">
          <SearchBar v-if="!isLoginPage" class="hidden md:block" />

          <template v-if="isAuthenticated">
            <div>
              <!-- Account menu -->
              <div ref="loginMenu" class="relative flex flex-row items-center gap-x-1">
                <!-- Operator -->
                <template v-if="operator">
                  <span class="uppercase">{{ operator.contact?.fullName || operator.userName }}</span>
                  <span class="text-neutral-400 uppercase">
                    {{ $t("shared.layout.header.top_header.logged_in_as") }}
                  </span>
                </template>

                <button
                  type="button"
                  class="flex cursor-pointer items-center gap-1 text-sm font-medium leading-none
                        text-[--header-bottom-link-color]
                        hover:text-[--header-bottom-link-hover-color]"
                  @click="loginMenuVisible = !loginMenuVisible"
                >
                  <!-- Icono usuario -->
                  <VcIcon
                    name="user"
                    class="shrink-0 text-[--header-bottom-link-color] [--vc-icon-size:1.1rem]"
                  />

                  <!-- Nombre -->
                  <span class="leading-none uppercase">
                    <template v-if="isMultiOrganization">
                      {{ organization?.name }} /
                    </template>
                    {{ user.contact?.fullName || user.userName }}
                  </span>

                  <!-- Chevron -->
                  <VcIcon
                    class="ms-1 text-[--header-bottom-link-color] [--vc-icon-size:0.625rem]"
                    :name="loginMenuVisible ? 'chevron-up' : 'chevron-down'"
                  />
                </button>

                <div
                  v-if="loginMenuVisible"
                  class="absolute right-0 top-full z-10 flex w-60 flex-col rounded-md bg-additional-50 text-additional-950 shadow-md"
                >
                  <div class="flex items-center justify-between p-3">
                    <router-link
                      to="/account/dashboard"
                      class="flex items-center font-bold text-[--header-bottom-link-color] hover:text-[--header-bottom-link-hover-color] bottom-header-link flex flex-row items-center gap-0.5 px-3 text-sm tracking-wide"
                      @click="loginMenuVisible = false"
                    >
                      <VcIcon class="text-primary" name="user-circle"/>

                      <span class="ml-2 uppercase">
                        {{ user.contact?.fullName || user.userName }}
                      </span>
                    </router-link>

                    <div class="grow"></div>

                    <VcButton
                      :title="$t('shared.layout.header.link_logout')"
                      class="ml-4"
                      variant="outline"
                      color="secondary"
                      size="xs"
                      icon
                      @click="() => signMeOut()"
                    >
                      <VcIcon name="logout" />
                    </VcButton>
                  </div>

                  <div v-if="isMultiOrganization" class="border-t py-3">
                    <div class="px-3 py-1 text-xs text-neutral-600">
                      {{ $t("common.labels.organizations") }}
                    </div>

                    <VcRadioButton
                      v-for="item in user.contact?.organizations?.items"
                      :key="item.id"
                      v-model="contactOrganizationId"
                      :label="item.name"
                      :value="item.id"
                      class="flex px-3 py-1 text-sm"
                      @change="selectOrganization"
                    />
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- Unauthorized menu items -->
          <template v-else>
            <div class="flex items-center">

              <VcButton
                variant="header"
                color="neutral"
                size="sm"
                class="text-sm text-secondary focus:outline-none rounded uppercase"
                :class="{ 'bg-white': isActive('/sign-in') }"
                @click="goToSignIn(route.fullPath)"
              >
                {{ $t("shared.layout.header.link_sign_in") }}
              </VcButton>


              <span class="mx-3 size-1 rounded-full bg-secondary" />

              <VcButton
                to="/sign-up"
                variant="header"
                color="neutral"
                size="sm"
                class="text-sm text-secondary focus:outline-none rounded uppercase"
                :class="{ 'bg-white': isActive('/sign-up') }"
              >
                {{ $t("shared.layout.header.link_register_now") }}
              </VcButton>
            </div>
          </template>
        </div>
      </div>
  </div>
</template>


<script setup lang="ts">
import { onClickOutside } from "@vueuse/core";
import { computed, ref } from "vue";
import { useUser, useSignMeOut, SignInForm } from "@/shared/account";
import { SearchBar } from "@/shared/layout";
import { useRoute, useRouter } from "vue-router";
import CurrencySelector from "@/shared/layout/components/currency-selector/currency-selector.vue";
import LanguageSelector from "@/shared/layout/components/language-selector/language-selector.vue";
import { AIODP_URL, BN_BACKEND_AUTH_URL, BN_BACKEND_URL } from "@/core/constants";
import { useSignIn } from "@/shared/sign-in/composables/useSignIn";

const router = useRouter();
const route = useRoute();
const { goToSignIn } = useSignIn();
const loginMenu = ref(null);
const loginMenuVisible = ref(false);
const { isAuthenticated, isMultiOrganization, isCorporateMember, user, operator, organization, switchOrganization } = useUser();
const { signMeOut } = useSignMeOut();

const hiddenRoutes = ["SignIn", "SignUp", "ForgotPassword"];
const hiddenPaths = ["/sign-in", "/sign-up", "/forgot-password"]

const isLoginPage = computed(() => hiddenRoutes.includes(String(route.name)) || hiddenPaths.includes(route.path));
const contactOrganizationId = ref(user.value?.contact?.organizationId);

const activeHeaderButton = ref<'marketplace' | 'business_navigator'>('marketplace');

function isActive(paths: string | string[]) {
  return Array.isArray(paths)
    ? paths.includes(route.path)
    : route.path === paths;
}

type RedirectTarget = "marketplace" | "business_navigator";

async function redirect(target: RedirectTarget) {

  if (target === 'marketplace') {
    router.push({ name: 'Catalog' });
    return;
  }
  else if (target === 'business_navigator' && isAuthenticated.value) {
    window.location.href = BN_BACKEND_URL + '/sso/v2';
  }
  else {
    window.location.href = BN_BACKEND_URL;
  }
}

function getExternalLink() {
  return AIODP_URL;
}

async function selectOrganization(): Promise<void> {
  if (!contactOrganizationId.value) {
    return;
  }

  await switchOrganization(contactOrganizationId.value);
  loginMenuVisible.value = false;
}


onClickOutside(loginMenu, () => {
  loginMenuVisible.value = false;
});
</script>
